/**
 * The five tones the product reasons in, and the classes each one is drawn with.
 *
 * `index.css` has carried the tokens since the palette was set, but nothing named
 * them: every tinted pill, box and card edge wrote the class triple out by hand,
 * and the copies drifted until one control bordered its warning ground with the
 * saturated fill colour while its sibling used the rule. Naming them here makes
 * that a single decision instead of a per-site one.
 *
 * Tones are named for their tokens rather than for their mood. A `danger` that
 * resolves to `--color-destructive-*` is one more mapping to hold in the head.
 */
export const TONES = [
  "success",
  "warning",
  "info",
  "destructive",
  "provisional",
  "neutral",
] as const;

export type Tone = (typeof TONES)[number];

/**
 * Where a tone is being applied, which decides which tokens it reaches for.
 *
 * `surface` is the tinted box or pill and the one most things want. `edge` is the
 * accent stripe down a card, drawn in the saturated colour because it is a rule
 * rather than a ground. `ground` is the faint wash a card lays under that stripe,
 * and is deliberately separate: the disclosure tone takes an edge and no wash, and
 * folding the two would give it one. `words` is the tone's text colour alone, for a
 * line that says something in the tone with no box around it. `wash` fades the tint
 * out across a row's head, for a row whose edge already says its state. `line` is the tone's
 * border alone, for a card that keeps its own ground, as a confirmed plan does.
 */
export type ToneSurface =
  "surface" | "edge" | "ground" | "wash" | "mark" | "words" | "line";

/**
 * A total record over both axes. A tone added to `TONES` without a row here fails
 * to compile, the way the served-enum tables in the features already work.
 *
 * Every tone reads its text off its `-strong` token, the design's darker type on
 * its own ground. Neutral is not a tone family at all, it is the page's own greys, which is why
 * its row names `border`, `muted` and `muted-foreground` rather than a tone.
 */
const TONE_CLASSES: Record<Tone, Record<ToneSurface, string>> = {
  success: {
    surface: "border-success-border bg-success-surface text-success-strong",
    edge: "border-l-success",
    ground: "bg-success-surface/60",
    wash: "bg-linear-to-r from-success-surface to-card to-46%",
    mark: "bg-success-surface text-success-strong border-b-success",
    words: "text-success-strong",
    line: "border-success-border",
  },
  warning: {
    surface: "border-warning-border bg-warning-surface text-warning-strong",
    edge: "border-l-warning",
    ground: "bg-warning-surface/60",
    wash: "bg-linear-to-r from-warning-surface to-card to-46%",
    mark: "bg-warning-surface text-warning-strong border-b-warning",
    words: "text-warning-strong",
    line: "border-warning-border",
  },
  info: {
    surface: "border-info-border bg-info-surface text-info-strong",
    edge: "border-l-info",
    ground: "bg-info-surface/60",
    wash: "bg-linear-to-r from-info-surface to-card to-46%",
    mark: "bg-info-surface text-info-strong border-b-info",
    words: "text-info-strong",
    line: "border-info-border",
  },
  destructive: {
    surface:
      "border-destructive-border bg-destructive-surface text-destructive-strong",
    edge: "border-l-destructive",
    ground: "bg-destructive-surface/60",
    wash: "bg-linear-to-r from-destructive-surface to-card to-46%",
    mark: "bg-destructive-surface text-destructive-strong border-b-destructive",
    words: "text-destructive-strong",
    line: "border-destructive-border",
  },
  provisional: {
    surface:
      "border-provisional-border bg-provisional-surface text-provisional-strong",
    edge: "border-l-provisional",
    ground: "bg-provisional-surface/60",
    wash: "bg-linear-to-r from-provisional-surface to-card to-46%",
    mark: "bg-provisional-surface text-provisional-strong border-b-provisional",
    words: "text-provisional-strong",
    line: "border-provisional-border",
  },
  neutral: {
    surface: "border-border bg-muted text-muted-foreground",
    edge: "border-l-border",
    ground: "bg-muted/60",
    wash: "bg-linear-to-r from-muted to-card to-46%",
    mark: "bg-muted text-muted-foreground border-b-border",
    words: "text-muted-foreground",
    line: "border-border",
  },
};

export function toneClasses(tone: Tone, surface: ToneSurface): string {
  return TONE_CLASSES[tone][surface];
}
